import React, { useRef, useEffect, useState, useCallback } from "react";
import styles from "./NavBar.module.scss";
import Tooltip from "../../../UI/ToolTip/ToolTip.js";

interface NavBarProps {
  setIsActive: (tabName: string) => void;
  isActive: string;
  myPerson?: boolean;
  setInfoReady: (ready: boolean) => void;
  embedded?: boolean;
  showMaterialesTab?: boolean;
}

const HINT_CLOSE_MS = 180;
const HOVER_HINTS_MEDIA = "(hover: hover) and (pointer: fine)";

const NavBar: React.FC<NavBarProps> = ({
  setIsActive,
  isActive,
  myPerson,
  setInfoReady,
  embedded = false,
  showMaterialesTab = false,
}) => {
  const navRef = useRef<HTMLElement | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [hintSectionName, setHintSectionName] = useState<string | null>(null);
  const [hintCoords, setHintCoords] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [hoverHintsCapable, setHoverHintsCapable] = useState(false);

  const sectionsConfig: Array<{
    name: string;
    shortName: string;
    hint: string;
    condition?: boolean;
  }> = [
    {
      name: "Resumen",
      shortName: "Resumen",
      hint: "Datos generales, presentación y accesos rápidos de este perfil.",
    },
    {
      name: "Materiales",
      shortName: "Materiales",
      hint: "Listado de materiales publicado por este perfil (vista de referencia).",
      condition: showMaterialesTab === true,
    },
    {
      name: "Productos/Servicios",
      shortName: "Prod. y serv.",
      hint: "Catálogo de productos y servicios publicados en el marketplace.",
    },
    {
      name: "Oportunidades",
      shortName: "Oportunidades",
      hint: "Proyectos privados visibles y solicitudes de materiales públicas en este perfil.",
    },
    {
      name: "Calificaciones",
      shortName: "Reseñas",
      hint: "Opiniones y calificaciones de quienes trabajaron con este perfil.",
    },
    {
      name: "Muro",
      shortName: "Muro",
      hint: "Publicaciones y novedades compartidas en el estilo de un feed.",
    },
    {
      name: "Gestión",
      shortName: "Gestión",
      condition: myPerson === true,
      hint: "Solo vos: cotizaciones, accesos a obras, documentación y proyectos para administrar.",
    },
  ];

  const sections = sectionsConfig.filter((section) => {
    const hasCondition = Object.prototype.hasOwnProperty.call(
      section,
      "condition"
    );
    return !hasCondition || section.condition === true;
  });

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleCloseHint = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setHintSectionName(null);
      setHintCoords(null);
      closeTimerRef.current = null;
    }, HINT_CLOSE_MS);
  }, [clearCloseTimer]);

  const openHint = useCallback(
    (sectionName: string, anchorEl: HTMLElement) => {
      if (
        typeof window === "undefined" ||
        !window.matchMedia(HOVER_HINTS_MEDIA).matches
      ) {
        return;
      }
      clearCloseTimer();
      const r = anchorEl.getBoundingClientRect();
      setHintSectionName(sectionName);
      setHintCoords({
        top: r.bottom + 6,
        left: r.left + r.width / 2,
      });
    },
    [clearCloseTimer]
  );

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return;
    }
    const mq = window.matchMedia(HOVER_HINTS_MEDIA);
    const sync = () => setHoverHintsCapable(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    if (!hoverHintsCapable) {
      clearCloseTimer();
      setHintSectionName(null);
      setHintCoords(null);
    }
  }, [hoverHintsCapable, clearCloseTimer]);

  useEffect(() => {
    if (!hintSectionName) return;
    const onScrollOrResize = () => {
      setHintSectionName(null);
      setHintCoords(null);
    };
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [hintSectionName]);

  useEffect(() => {
    return () => clearCloseTimer();
  }, [clearCloseTimer]);

  useEffect(() => {
    if (isActive && navRef.current) {
      const activeButton = navRef.current.querySelector(
        `.${styles.navBar_button_active}`
      );
      if (activeButton) {
        centerActiveButton(activeButton as HTMLElement);
      }
    }
  }, [isActive]);

  const centerActiveButton = (buttonElement: HTMLElement) => {
    if (!navRef.current || !buttonElement) return;

    const nav = navRef.current;
    const navRect = nav.getBoundingClientRect();
    const buttonRect = buttonElement.getBoundingClientRect();

    const buttonCenter = buttonRect.left + buttonRect.width / 2;
    const navCenter = navRect.left + navRect.width / 2;
    const scrollOffset = buttonCenter - navCenter;

    nav.scrollBy({
      left: scrollOffset,
      behavior: "smooth",
    });
  };

  const handleClick = (section: string, buttonElement: HTMLElement) => {
    setInfoReady(false);
    setIsActive(section);

    setTimeout(() => {
      centerActiveButton(buttonElement);
    }, 100);
  };

  const hintEntry = hintSectionName
    ? sections.find((s) => s.name === hintSectionName)
    : undefined;

  useEffect(() => {
    if (hintSectionName && !hintEntry) {
      setHintSectionName(null);
      setHintCoords(null);
    }
  }, [hintSectionName, hintEntry]);

  return (
    <div className={`${styles.navBar} ${embedded ? styles.navBar_embedded : ""}`}>
      <div className={styles.navBar_row}>
        <div className={styles.navBar_wrapper}>
          <nav ref={navRef} className={styles.navBar_nav}>
            {sections.map((section) => (
              <button
                key={section.name}
                type="button"
                className={`${styles.navBar_button} ${
                  isActive === section.name ? styles.navBar_button_active : ""
                }`}
                onClick={(e) => handleClick(section.name, e.currentTarget)}
                {...(hoverHintsCapable
                  ? {
                      onMouseEnter: (e: React.MouseEvent<HTMLButtonElement>) =>
                        openHint(section.name, e.currentTarget),
                      onMouseLeave: scheduleCloseHint,
                    }
                  : {})}
              >
                <span className={styles.navBar_button_label}>
                  {section.shortName}
                </span>
              </button>
            ))}
          </nav>
        </div>
      </div>
      {hoverHintsCapable && hintEntry && hintCoords && (
        <div
          className={styles.navBar_hintAnchor}
          style={{
            top: hintCoords.top,
            left: hintCoords.left,
          }}
          onMouseEnter={clearCloseTimer}
          onMouseLeave={scheduleCloseHint}
        >
          <Tooltip
            text={hintEntry.hint}
            fromNav={false}
            fromHeaderNav={false}
            inlineDock={false}
            profileHint
          />
        </div>
      )}
    </div>
  );
};

export default NavBar;
